import React from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { semantic } from "../../theme/tokens";
import { alpha, contrastInk } from "../../theme/color";

/**
 * Chip de estado semântico.
 *
 * Duas regras que o Chip cru do MUI não dá:
 *  1. o status vira cor por significado (pago = sucesso, vencido = perigo),
 *     em vez de `color="primary"` escolhido caso a caso na página;
 *  2. cor cadastrada pelo usuário (status/tag/origem) é renderizada com o hex
 *     exato do banco como preenchimento — é a identidade que a pessoa
 *     configurou e ela precisa bater em toda tela onde a tag aparece. Só a
 *     tinta do texto se adapta ao contraste, o que resolve o caso da tag
 *     amarela que antes saía com texto branco sobre amarelo.
 */

const TONE_BY_STATUS = {
  // Financeiro
  paid: "success",
  received: "success",
  partially_paid: "info",
  open: "neutral",
  pending: "neutral",
  awaiting: "warning",
  overdue: "danger",
  canceled: "neutral",
  cancelled: "neutral",
  refunded: "warning",
  // Cliente
  active: "success",
  inactive: "neutral",
  blocked: "danger",
  // Negócios
  won: "success",
  lost: "danger",
  // Genéricos de execução
  running: "info",
  scheduled: "info",
  paused: "warning",
  error: "danger",
  failed: "danger",
  processed: "success",
  duplicated: "neutral",
};

const LABEL_BY_STATUS = {
  paid: "Pago",
  received: "Recebido",
  partially_paid: "Parcial",
  open: "Em aberto",
  pending: "Pendente",
  awaiting: "Aguardando",
  overdue: "Vencido",
  canceled: "Cancelado",
  cancelled: "Cancelado",
  refunded: "Estornado",
  active: "Ativo",
  inactive: "Inativo",
  blocked: "Bloqueado",
  won: "Ganho",
  lost: "Perdido",
  running: "Em execução",
  scheduled: "Agendado",
  paused: "Pausado",
  error: "Erro",
  failed: "Falhou",
  processed: "Processado",
  duplicated: "Duplicado",
};

const useStyles = makeStyles(() => ({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    height: 22,
    padding: "0 8px",
    borderRadius: 6,
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    border: "1px solid",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: "currentColor",
  },
  clickable: {
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { filter: "brightness(0.97)" },
  },
  muted: {
    opacity: 0.55,
  },
  // Botão de remover: herda a tinta do chip via currentColor, então continua
  // legível em qualquer cor cadastrada no banco.
  remove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    marginRight: -2,
    padding: 0,
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    background: "transparent",
    color: "inherit",
    opacity: 0.7,
    fontSize: 13,
    lineHeight: 1,
    flexShrink: 0,
    "&:hover": { opacity: 1, backgroundColor: "currentColor" },
    "&:hover > span": { color: "#fff" },
  },
}));

export const statusLabel = (status) =>
  LABEL_BY_STATUS[status] || (status ? String(status) : "—");

export const statusTone = (status) => TONE_BY_STATUS[status] || "neutral";

const StatusChip = ({
  status,
  label,
  color,
  tone,
  dot,
  className,
  onClick,
  onDelete,
  selected,
  disabled,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const resolvedTone = tone || statusTone(status);
  const resolvedLabel = label || statusLabel(status);

  // `selected === false` é o estado "disponível mas não aplicado" do seletor
  // de tags: mesma cor, intensidade menor. Sem isso o não-selecionado viraria
  // cinza e perderia a identidade da tag.
  const isDeselected = selected === false;

  // Cor cadastrada pelo usuário (tag, status, origem) é renderizada CHEIA, com
  // o hex exato do banco — é a identidade que a pessoa configurou e ela tem
  // que bater em toda tela. Só a tinta do texto se adapta, pelo contraste.
  // Sem `color`, cai na paleta semântica em tinta suave.
  const style = color
    ? {
        color: contrastInk(color),
        backgroundColor: color,
        borderColor: color,
      }
    : (() => {
        const base = (semantic[resolvedTone] || semantic.neutral)[
          isDark ? "onDark" : "main"
        ];
        return {
          color: base,
          backgroundColor: alpha(base, isDark ? 0.16 : 0.1),
          borderColor: alpha(base, isDark ? 0.32 : 0.22),
        };
      })();

  return (
    <span
      className={[
        classes.chip,
        onClick && !disabled ? classes.clickable : null,
        isDeselected || disabled ? classes.muted : null,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      onClick={disabled ? undefined : onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyPress={
        onClick && !disabled
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick(e);
            }
          : undefined
      }
    >
      {dot && <span className={classes.dot} />}
      {resolvedLabel}
      {onDelete && !disabled && (
        <button
          type="button"
          className={classes.remove}
          aria-label={`Remover ${typeof resolvedLabel === "string" ? resolvedLabel : "item"}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(e);
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </span>
  );
};

StatusChip.propTypes = {
  /** Chave de status conhecida (ex.: "paid", "overdue", "active"). */
  status: PropTypes.string,
  /** Sobrescreve o rótulo traduzido. */
  label: PropTypes.node,
  /** Cor hex customizada — usada por status/tags/origens cadastrados no banco. */
  color: PropTypes.string,
  /** Força um tom semântico ignorando o mapa de status. */
  tone: PropTypes.oneOf(["success", "warning", "danger", "info", "neutral"]),
  /** Exibe o marcador circular à esquerda. */
  dot: PropTypes.bool,
  className: PropTypes.string,
  /** Torna o chip acionável. */
  onClick: PropTypes.func,
  /** Renderiza o botão de remover (×). */
  onDelete: PropTypes.func,
  /** `false` esmaece o chip mantendo a cor — estado "não aplicado". */
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
};

StatusChip.defaultProps = {
  status: undefined,
  label: undefined,
  color: undefined,
  tone: undefined,
  dot: false,
  className: undefined,
  onClick: undefined,
  onDelete: undefined,
  selected: undefined,
  disabled: false,
};

export default StatusChip;
